import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import { createPortal } from 'react-dom';

/**
 * One tooltip per grid, for header tooltips and cut-off cell text. It lives in
 * its own component so showing it does not re-render the grid's rows.
 *
 * WCAG 1.4.13: Escape dismisses it, the pointer can move onto it without it
 * closing, and it stays until the pointer leaves, a key or scroll closes it.
 * Shown after a short hover delay; the keyboard shows header tooltips at once.
 */

export interface TipApi {
  /** Show `text` under `anchor` after `delay` ms (default 500). */
  show: (text: string, anchor: HTMLElement, delay?: number) => void;
  hide: () => void;
  /** The element the tooltip is shown (or pending) for. */
  anchor: () => HTMLElement | null;
  /** True when `el` is inside the tooltip itself. */
  contains: (el: Node | null) => boolean;
}

interface Tip {
  text: string;
  x: number;
  y: number;
  /** The anchor's top, for flipping above when there is no room below. */
  top: number;
}

export function GridTooltip({ apiRef, id }: { apiRef: Ref<TipApi>; id: string }) {
  const [tip, setTip] = useState<Tip | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const elRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(apiRef, () => {
    const clear = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
    return {
      show: (text, anchor, delay = 500) => {
        clear();
        anchorRef.current = anchor;
        const open = () => {
          timer.current = null;
          if (!anchor.isConnected) return;
          const r = anchor.getBoundingClientRect();
          setTip({ text, x: r.left, y: r.bottom + 4, top: r.top });
        };
        if (delay <= 0) open();
        else timer.current = setTimeout(open, delay);
      },
      hide: () => {
        clear();
        anchorRef.current = null;
        setTip(null);
      },
      anchor: () => anchorRef.current,
      contains: (el) => !!el && !!elRef.current?.contains(el),
    };
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Escape dismisses; a scroll or a pointer press closes; leaving the tooltip
  // (other than back onto its anchor) closes.
  useEffect(() => {
    if (!tip) return;
    const close = () => {
      anchorRef.current = null;
      setTip(null);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const leave = (e: MouseEvent) => {
      const to = e.relatedTarget;
      if (!(to instanceof Node) || !anchorRef.current?.contains(to)) close();
    };
    const el = elRef.current;
    document.addEventListener('keydown', key, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('pointerdown', close, true);
    el?.addEventListener('mouseleave', leave);
    return () => {
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('pointerdown', close, true);
      el?.removeEventListener('mouseleave', leave);
    };
  }, [tip]);

  // Keep it on screen: pull it in from the right edge, flip above the anchor at the bottom.
  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el || !tip) return;
    const r = el.getBoundingClientRect();
    if (r.right > window.innerWidth - 4) el.style.left = `${Math.max(4, window.innerWidth - 4 - r.width)}px`;
    if (r.bottom > window.innerHeight - 4 && tip.top - r.height - 4 >= 0) el.style.top = `${tip.top - r.height - 4}px`;
  }, [tip]);

  if (!tip || typeof document === 'undefined') return null;
  return createPortal(
    <div ref={elRef} id={id} role="tooltip" className="app-grid-tip" style={{ left: tip.x, top: tip.y }}>
      {tip.text}
    </div>,
    document.body,
  );
}
